import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import {
  useLoginSuperAdminMutation,
  useLoginBusinessAdminMutation,
} from "../../store/api";
import { setCredentials } from "../../store/authSlice";
import { clearLicenseExpired, setLicenseExpired } from "../../store/licenseSlice";
import { isLicenseExpiredProblem } from "../../utils/problemDetails";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loginSuperAdmin, superAdminState] = useLoginSuperAdminMutation();
  const [loginBusinessAdmin, businessAdminState] =
    useLoginBusinessAdminMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loginError, setLoginError] = useState(null);
  const isLoading = superAdminState.isLoading || businessAdminState.isLoading;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoginError(null);

    try {
      // Try super admin login first
      const result = await loginSuperAdmin(form).unwrap();
      const token = result?.accessToken || result?.token;
      const user = result?.user || result?.profile || null;
      dispatch(setCredentials({ token, user }));
      dispatch(clearLicenseExpired());

      // Redirect based on user role
      if (user?.isPlatformAdmin === true) {
        navigate("/businesses");
      } else {
        navigate("/menu/categories");
      }
    } catch (superAdminError) {
      // If super admin login fails, try business admin login
      try {
        const result = await loginBusinessAdmin(form).unwrap();
        const token = result?.accessToken || result?.token;
        const user = result?.user || result?.profile || null;
        dispatch(setCredentials({ token, user }));
        dispatch(clearLicenseExpired());

        // Redirect based on user role
        if (user?.isPlatformAdmin === true) {
          navigate("/businesses");
        } else {
          navigate("/menu/categories");
        }
      } catch (businessAdminError) {
        if (isLicenseExpiredProblem(businessAdminError)) {
          dispatch(
            setLicenseExpired({
              code: "LICENSE_EXPIRED",
              message:
                businessAdminError?.data?.detail ||
                "License expired. Please renew to continue.",
            }),
          );
          navigate("/license-expired", { replace: true });
          return;
        }
        // Both failed, show error
        setLoginError(
          businessAdminError?.data?.detail ||
            superAdminError?.data?.detail ||
            "Invalid email or password. Please try again.",
        );
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] px-4 py-12">
      <div className="mx-auto w-full max-w-xl sm:p-10">
        <Card>
          <h1 className="text-center text-2xl font-semibold text-slate-900">
            Admin Portal
          </h1>
          <p className="mt-2 text-center text-sm text-slate-600">
            Sign in to manage your restaurant operations
          </p>

          <form className="mt-8 flex flex-col gap-5" onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              name="email"
              placeholder="admin@example.com"
              value={form.email}
              onChange={handleChange}
              required
            />

            <Input
              label="Password"
              type="password"
              name="password"
              placeholder="Enter your password"
              value={form.password}
              onChange={handleChange}
              required
            />

            {loginError && <Alert tone="error">{loginError}</Alert>}

            <Button type="submit" className="mt-2 w-full" disabled={isLoading}>
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
